import {
  fetchExchange,
  errorExchange,
  Exchange,
  subscriptionExchange,
} from 'urql';
import { makeOperation } from '@urql/core';
import { devtoolsExchange } from '@urql/devtools';
import { refocusExchange } from '@urql/exchange-refocus';
import { AuthClient } from '@appcues/auth';
import { authExchange } from '@urql/exchange-auth';
import { Client as WSClient, SubscribePayload } from 'graphql-ws';
import { reportError } from 'studio5/lib/error-reporting';

type OtherExchange = {
  cacheExchange: Exchange;
  wsClient?: WSClient;
};

export function setupExchanges(
  auth: AuthClient,
  other: OtherExchange
): Exchange[] {
  const { cacheExchange, wsClient } = other;

  const exchanges: Exchange[] = [
    refocusExchange(),
    errorExchange({
      onError: error => {
        if (error.networkError) {
          return reportError(error.networkError);
        }

        if (error.graphQLErrors) {
          return reportError(
            new Error(
              error.graphQLErrors.reduce((acc, curr) => {
                return acc + curr.message;
              }, '')
            )
          );
        }

        return reportError(error);
      },
    }),
    authExchange(async () => {
      let token = await auth.getToken();

      return {
        addAuthToOperation(operation) {
          if (!token) {
            return operation;
          }

          const fetchOptions =
            typeof operation.context.fetchOptions === 'function'
              ? operation.context.fetchOptions()
              : operation.context.fetchOptions || {};

          return makeOperation(operation.kind, operation, {
            ...operation.context,
            fetchOptions: {
              ...fetchOptions,
              headers: {
                ...fetchOptions.headers,
                Authorization: `Bearer ${token}`,
              },
            },
          });
        },
        didAuthError(error) {
          return error.message.includes('401');
        },
        refreshAuth: async () => {
          const newToken = await auth.getToken();
          token = newToken;
        },
      };
    }),
    cacheExchange,
    fetchExchange,
  ];

  if (wsClient) {
    exchanges.push(
      subscriptionExchange({
        forwardSubscription(operation) {
          return {
            subscribe: sink => {
              const dispose = wsClient.subscribe(
                operation as SubscribePayload,
                sink
              );
              return {
                unsubscribe: dispose,
              };
            },
          };
        },
      })
    );
  }

  if (process.env.NODE_ENV === 'development') {
    exchanges.unshift(devtoolsExchange);
  }

  return exchanges;
}
